import { Trans, useLingui } from "@lingui/react";
import { Button } from "@warp-ds/elements/react/button";
import { useAdViewAd } from "src/context/AdViewProviderContext.tsx";
import { useAppConfig } from "src/context/AppConfigProviderContext.tsx";
import { Icon } from "../Icon.tsx";
import { useGallery } from "./state.tsx";

export const Actions = () => {
  const { i18n } = useLingui();
  const { images } = useAdViewAd();
  const { isMobile } = useAppConfig();
  const {
    currentImageIndex,
    isOnAdSlide,
    hasOpenedFullscreen,
    inCarouselAdLoaded,
    nonFullscreenAdLoaded,
    showNextImage,
    showPreviousImage,
    fullScreen,
  } = useGallery();

  // Only count the ad slide once it has actually loaded (mockup or real) — otherwise a
  // placement with no fill (e.g. no ad server in local/dev) would permanently inflate the
  // count and show navigation arrows for an ad slide that never appears. The non-fullscreen
  // ad slide is also gone for good once fullscreen has been opened, so don't count it then
  // even if it was loaded earlier.
  const adLoaded = fullScreen ? inCarouselAdLoaded : !hasOpenedFullscreen && nonFullscreenAdLoaded;
  const willShowAdSlide = images.length > 0 && adLoaded;
  const totalNoOfImages = images.length + (willShowAdSlide ? 1 : 0);

  const displayIndex = isOnAdSlide ? totalNoOfImages : currentImageIndex + 1;
  const imageText = isOnAdSlide ? "" : images[currentImageIndex]?.description || "";
  const imageLabel = i18n.t({
    id: "imageGallery.description",
    message: "Image {current} of {total}",
    values: { current: currentImageIndex + 1, total: images.length },
  });

  return (
    <>
      {totalNoOfImages > 1 ? (
        <div className="absolute inset-0 w-full h-full flex justify-between items-center px-16 pointer-events-none z-30">
          <Button
            type="button"
            variant="pill"
            aria-label={i18n.t({ id: "imageGallery.gallery.previous", message: "Go to previous image" })}
            className="pointer-events-auto min-w-48 min-h-48 part-[base]:!bg-[--w-black/70] part-[base]:!hover:bg-[--w-black/90] part-[base]:!s-text-inverted-static transition-transform duration-100 ease-in-out part-[base]:active:scale-95 z-30"
            onClick={showPreviousImage}
          >
            <Icon name="ChevronLeft" size="medium" />
          </Button>
          <Button
            type="button"
            variant="pill"
            aria-label={i18n.t({ id: "imageGallery.gallery.next", message: "Go to next image" })}
            className="pointer-events-auto min-w-48 min-h-48 part-[base]:!bg-[--w-black/70] part-[base]:!hover:bg-[--w-black/90] part-[base]:!s-text-inverted-static transition-transform duration-100 ease-in-out part-[base]:active:scale-95 z-30"
            onClick={showNextImage}
          >
            <Icon name="ChevronRight" size="medium" />
          </Button>
        </div>
      ) : null}

      <div className="absolute text-center bottom-16 flex flex-col justify-center items-center w-full z-30 pointer-events-none">
        {images.length > 0 ? (
          <output
            aria-live="polite"
            aria-label={`${imageText} ${imageLabel}`}
            className="rounded-8 p-8 !bg-[--w-black/70] !s-text-inverted-static truncate mx-16 max-w-full pointer-events-auto"
          >
            <span aria-hidden>{!isOnAdSlide && imageText ? `${imageText} ` : null}</span>
            <span aria-hidden>{`(${displayIndex}/${totalNoOfImages})`}</span>
          </output>
        ) : null}
      </div>
      {fullScreen && !isMobile ? (
        <div className="absolute text-center top-16 flex flex-col justify-center items-center w-full z-30 disappear pointer-events-none">
          <div className="rounded-8 p-8 !bg-[--w-black/70] !s-text-inverted-static mx-16 pointer-events-auto">
            <Trans
              id="imageGallery.fullscreen"
              message={'Use the arrow keys to switch between images, and "Escape" to close fullscreen'}
            />
          </div>
        </div>
      ) : null}
    </>
  );
};
